// 🧱 React & MUI imports
import React, { useState } from "react";
import {
  Container,
  TextField,
  Typography,
  Paper,
  Stack,
  Box,
  Fab,
  Snackbar,
  Alert
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

// 🔥 Firebase function to add a new category
import { addCategory } from "../../firebase-handler";

// 📦 RTL (Right-to-Left) support via Emotion + MUI
import { createTheme, ThemeProvider } from "@mui/material/styles";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// 🎨 Custom CSS
import "../CSS/AddCategory.css";

const AddCategoryPage = () => {
  // 📌 State for input fields
  const [categoryName, setCategoryName] = useState("");   // Stores the entered category name
  const [material, setMaterial] = useState("");           // Stores the entered study material

  // 🌐 Theme and RTL cache setup
  // Create a custom MUI theme with Right-to-Left direction
  const theme = createTheme({ direction: "rtl" });

  // Configure Emotion cache with RTL plugin for proper styling in RTL layout
  const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
  });

  // 🔔 Snackbar state for displaying feedback messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);              // Controls Snackbar visibility
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");  // Message type: success | error | warning | info
  const [snackbarMessage, setSnackbarMessage] = useState("");           // Content of the message displayed in Snackbar

  // 💾 Handle the Save button click
  const handleSave = async () => {
    try {
      // 🛑 Validation: Ensure the category name is not empty
      if (categoryName.trim() === "") {
        setSnackbarSeverity("error");
        setSnackbarMessage("שם קטגוריה לא יכול להיות ריק!");
        setSnackbarOpen(true);
        return;
      }

      // 🛑 Validation: Ensure the study material is not empty
      if (material.trim() === "") {
        setSnackbarSeverity("error");
        setSnackbarMessage("חומר לימוד לא יכול להיות ריק!");
        setSnackbarOpen(true);
        return;
      }

      // Add the category to Firestore
      const categoryId = await addCategory(categoryName, material);
      console.log("Category added successfully with ID:", categoryId);
      // Notify user of successful addition
      setSnackbarSeverity("success");
      setSnackbarMessage(`קטגוריה "${categoryName}" נוספה בהצלחה!`);
      setSnackbarOpen(true);
      // Reset input fields after successful save
      setCategoryName("");
      setMaterial("");
    } catch (error) {
      // Error handling: Notify user and log the error
      console.error("Error adding category:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("שגיאה בהוספת הקטגוריה, נסה שוב.");
      setSnackbarOpen(true);
    }
  };

  return (
    // 🧠 Provide the RTL cache to Emotion
    <CacheProvider value={cacheRtl}>
      {/* 🌍 Apply the RTL theme using MUI ThemeProvider */}
      <ThemeProvider theme={theme}>
        <Container maxWidth="sm" className="custom-container" dir="rtl">
          {/* 📝 Paper container for form layout */}
          <Paper elevation={3} className="custom-paper" dir="rtl">
            {/* 📌 Page title */}
            <Typography
              variant="h4"
              gutterBottom
              className="custom-title"
              dir="rtl"
            >
              הוסף קטגוריה
            </Typography>

             {/* 🧾 Input form for category name and study material */}
            <Stack spacing={3} className="custom-stack" dir="rtl">
              {/* Input for category name */}
              <TextField
                label="שם הקטגוריה"
                variant="outlined"
                fullWidth
                value={categoryName}
                dir="rtl"
                onChange={(e) => setCategoryName(e.target.value)}
              />

              {/* Input for study material (multiline) */}
              <TextField
                label="חומר לימוד"
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                value={material}
                dir="rtl"
                inputProps={{ style: { textAlign: "right" } }}
                onChange={(e) => setMaterial(e.target.value)}
              />
            </Stack>

            {/* ➕ Floating Action Button to trigger save operation */}
            <Box className="fab-container">
              <Fab color="primary" aria-label="save" onClick={handleSave}>
                <SaveIcon />
              </Fab>
            </Box>

            {/* 🔔 Snackbar for displaying success/error messages */}
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={5000}
              onClose={() => setSnackbarOpen(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={() => setSnackbarOpen(false)}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
                variant="filled"
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </Paper>
        </Container>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default AddCategoryPage;
